/**
 * @file 残した記録をあとから直すための組み立て (写し取りと、写真の差し替え)。
 *
 * @what: 記録 1 件を「同じ id で送り直す入力」に写し取る (logToInput) と、写真を足す・
 *   変える・外すを入力に反映して、消すべき古い画像のパスを返す (applyPhotoEdit)。
 * @why: サーバの保存は同じ id への全項目の上書きなので、画面に出していない項目 (単元・領域・
 *   分数・きき分けの detail など) も一緒に送り直さないと、直した拍子に消える。写し取りを
 *   1 か所に置き、面ごとの編集フォームは自分が出している項目だけを上書きする。
 * @failure: アップロードの失敗は throw (呼び出し側が文言を出し、記録そのものは送らない =
 *   参照切れを作らない)。古い画像の後始末は保存が通ったあとに呼び出し側が行う。
 */

import type { StudyProgressLog, StudyProgressLogInput } from "../api/plans";
import { uploadStudyLogImage } from "./studyLogImage";

/** 記録 1 件を、そのまま送り直せる入力に写し取る。 */
export function logToInput(log: StudyProgressLog): StudyProgressLogInput {
  const input: StudyProgressLogInput = { id: log.id, date: log.date };
  if (log.material_id) input.material_id = log.material_id;
  if (log.unit_id) input.unit_id = log.unit_id;
  if (log.from_page) input.from_page = log.from_page;
  if (log.to_page) input.to_page = log.to_page;
  if (log.realm) input.realm = log.realm;
  if (log.minutes) input.minutes = log.minutes;
  if (log.motor_item_id) input.motor_item_id = log.motor_item_id;
  if (log.rating) input.rating = log.rating;
  if (log.memo) input.memo = log.memo;
  if (log.detail) input.detail = log.detail;
  if (log.image_path) input.image_path = log.image_path;
  if (log.image_enc) input.image_enc = log.image_enc;
  return input;
}

/** 編集フォームでの写真の扱い。 */
export interface PhotoEdit {
  /** 新しく選んだ写真。選んでいなければ null。 */
  photo: File | null;
  /** いま付いている写真を外す指定。 */
  removed: boolean;
}

/**
 * 写真の変更を入力に反映する。
 *
 * @what: 新しい写真があれば先に上げて差し替え、外す指定なら参照を落とす。戻り値の stale は
 *   保存が通ったあとに消す古い画像のパス (無ければ空文字)。
 * @why: 上げてから記録を送るのは、記録だけ先に残って画像が無い状態を作らないためである。
 *   新しい写真は平文なので、古い記録に暗号化の印 (image_enc) が付いていても引き継がない。
 */
export async function applyPhotoEdit(
  input: StudyProgressLogInput,
  familyId: string,
  edit: PhotoEdit,
): Promise<{ input: StudyProgressLogInput; stale: string }> {
  const next: StudyProgressLogInput = { ...input };
  const prev = next.image_path ?? "";
  if (edit.photo) {
    next.image_path = await uploadStudyLogImage(edit.photo, familyId, next.id);
    delete next.image_enc;
    return { input: next, stale: prev };
  }
  if (edit.removed && prev) {
    delete next.image_path;
    delete next.image_enc;
    return { input: next, stale: prev };
  }
  return { input: next, stale: "" };
}
